import type { NuxtApp } from '#app';
import { adminTokenCookieName, useAuthStore } from '~/store/auth';
import { useSnackbarStore } from '~/store/snackbar';

/* eslint-disable  @typescript-eslint/no-explicit-any */
export type ApiResult<T> = { result: T; error: any };

export type ApiError = {
  statusCode?: number;
  message: string;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  data?: {
    message: string | Record<string, string[]>;
  };
};

/**
 * Returns the most specific message that the error gives. The API sends an error code in
 * `message`, and `errors.<code>` translates it. The status code and the raw API text are the
 * next sources. The generic message is the last fallback.
 */
export function apiErrorMessage(apiError: ApiError | null | undefined, nuxtApp: NuxtApp = useNuxtApp()): string {
  const { t, te } = nuxtApp.$i18n;
  const { message } = apiError?.data || {};

  if (typeof message === 'string' && te(`errors.${message}`)) {
    return t(`errors.${message}`);
  }

  if (message && typeof message === 'object') {
    const contents: string[] = [];
    for (const key in message) {
      // Normalize backend errors
      const errors = message[key].map(msg => msg.replace(`The ${key} `, '').replaceAll(' ', '_').replaceAll('.', ''));
      for (const err of errors) {
        if (te(`errors.${err}`)) {
          contents.push(t(`errors.${err}`, { data: key }));
        }
      }
    }
    if (contents.length > 0) {
      return contents.join(' ');
    }
  }

  const status = apiError?.statusCode;
  if (!status) {
    return t('errors.api_network_error');
  }
  if (status === 403) {
    return t('errors.api_forbidden');
  }
  if (status >= 500) {
    return t('errors.api_server_error');
  }
  // A 4xx text that is not an error code is a readable sentence, for example a validation message.
  if (typeof message === 'string' && message) {
    return t('errors.api_request_failed', { detail: message });
  }
  return t('errors.general_api_error');
}

export async function handleApiError(apiError: ApiError, nuxtApp: NuxtApp) {
  const { t } = nuxtApp.$i18n;
  if (apiError.statusCode === 401) {
    // Redirection will be handle in onResponseError
    return;
  }
  if (apiError.statusCode === 404) {
    throw createError({
      statusCode: 404,
      statusMessage: t('errors.page_not_found'),
    });
  }

  const { showMessage } = useSnackbarStore();
  showMessage({ content: apiErrorMessage(apiError, nuxtApp), color: 'error' });
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export async function useMyFetch<T>(request: string, options: any = {}): Promise<ApiResult<T>> {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();

  const event = useRequestEvent(nuxtApp);
  const headers = useRequestHeaders();

  const ip =
    headers?.['x-real-ip'] ??
    headers?.['x-forwarded-for'] ??
    event?.node?.req?.socket?.remoteAddress ??
    event?.node?.req?.connection?.remoteAddress ??
    options.headers?.['x-real-ip'] ??
    options.headers?.['x-forwarded-for'];

  const token = useCookie<string>(adminTokenCookieName);
  const fetchOptions = {
    baseURL: config.public.baseURL,
    method: 'GET',
    ...options,
    headers: {
      Accept: 'application/json',
      // Allow Authorization header to be overridden (for refresh token requests)
      Authorization: options.headers?.Authorization || `Bearer ${token.value}`,
      ...(ip && { 'x-forwarded-for': ip }),
      ...options.headers,
    },
    onResponseError: (ctx: { response: { status: number } }) => {
      if (ctx.response?.status === 401) {
        nuxtApp.runWithContext(async () => {
          if (!useRoute().path.includes('/signin')) {
            await useAuthStore().resetAndGoToSignin();
          }
        });
      }
    },
  };

  try {
    const data = await $fetch<T>(request, fetchOptions);
    return { result: data, error: null };
  } catch (err: any) {
    const apiError: ApiError = {
      statusCode: err?.statusCode ?? err?.response?.status,
      message: err?.message ?? 'Unknown error',
      data: err?.data,
    };
    if (!options?.skipDefaultErrorHandling) {
      await handleApiError(apiError, nuxtApp);
    }
    return { result: null as T, error: apiError };
  }
}
